import graphql__db987107f4a153fd7a64 from "./__generated__/CustomerActivityView_customerAccount.graphql.ts";
import graphql__876588c1b5221d6cec94 from "./__generated__/CustomerActivityViewQuery.graphql.ts";
import graphql__c7c7666dfb7e5eda7702 from "./__generated__/CustomerActivityViewRefreshQuery.graphql.ts";
import graphql__9600812fac2af19c74c6 from "./__generated__/CustomerActivityViewLoadMoreQuery.graphql.ts";import * as React from "react";
import {useState, useCallback} from "react";
import {graphql, useLazyLoadQuery, fetchQuery, useRelayEnvironment} from "react-relay";
import Panel from "./presentational/Panel";
import ActivityTimeline from "./ActivityTimeline";
import LoadMoreButton from "./presentational/LoadMoreButton";
import {useFragment} from "react-relay/hooks";
import {useOutletContext} from "react-router";
import {useIntl} from "react-intl";
import {CustomerActivityViewQuery} from "./__generated__/CustomerActivityViewQuery.graphql";
import {useActivityStream} from "../hooks/useActivityStream";

type Props = {}

type Activity = {
    readonly id: string;
    readonly source: string;
    readonly type: string;
    readonly occurredAt: string;
    readonly metadata: any;
    readonly actorName: string | null;
    readonly body: string | null;
};

const PAGE_SIZE = 50;

const CustomerActivityView: React.FC<Props> = () => {
    const queryRef: any = useOutletContext();
    const intl = useIntl();
    const environment = useRelayEnvironment();

    const customerAccount = useFragment(
        graphql__db987107f4a153fd7a64,
        queryRef
    );

    const initialData = useLazyLoadQuery<CustomerActivityViewQuery>(
        graphql__876588c1b5221d6cec94,
        {customerAccountIdentifier: customerAccount.customerAccountIdentifier, first: PAGE_SIZE, offset: 0}
    );

    const [activities, setActivities] = useState<ReadonlyArray<Activity>>(
        initialData.customerAccountActivities?.activities || []
    );
    const [hasMore, setHasMore] = useState(initialData.customerAccountActivities?.hasMore || false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Subscribe to realtime activity updates via SSE
    const handleNewActivity = useCallback(() => {
        // Refetch the first page to get the new activity
        fetchQuery(
            environment,
            graphql__c7c7666dfb7e5eda7702,
            {customerAccountIdentifier: customerAccount.customerAccountIdentifier, first: PAGE_SIZE, offset: 0}
        ).subscribe({
            next: (data: any) => {
                const newActivities = data.customerAccountActivities?.activities || [];
                setActivities(newActivities);
                setHasMore(data.customerAccountActivities?.hasMore || false);
            }
        });
    }, [environment, customerAccount.customerAccountIdentifier]);

    useActivityStream({
        subjectType: 'customer_account',
        subjectIdentifier: customerAccount.customerAccountIdentifier,
        onActivity: handleNewActivity,
    });

    const loadMore = useCallback(() => {
        if (isLoadingMore || !hasMore) return;

        setIsLoadingMore(true);
        const newOffset = activities.length;

        fetchQuery(
            environment,
            graphql__9600812fac2af19c74c6,
            {customerAccountIdentifier: customerAccount.customerAccountIdentifier, first: PAGE_SIZE, offset: newOffset}
        ).subscribe({
            next: (data: any) => {
                const newActivities = data.customerAccountActivities?.activities || [];
                setActivities(prev => [...prev, ...newActivities]);
                setHasMore(data.customerAccountActivities?.hasMore || false);
                setIsLoadingMore(false);
            },
            error: () => {
                setIsLoadingMore(false);
            }
        });
    }, [environment, customerAccount.customerAccountIdentifier, activities.length, hasMore, isLoadingMore]);

    return (
        <Panel headline={intl.formatMessage({id: 'tab.activities', defaultMessage: 'Activities'})}>
            <ActivityTimeline activities={activities} />
            {hasMore && (
                <LoadMoreButton onClick={loadMore} isLoadingNext={isLoadingMore} />
            )}
        </Panel>
    );
}

export default CustomerActivityView;
